// src/features/blog/service/blogService.js
import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
} from "../../../config/firebase";
import { BLOG_STATUS } from "../types/blogTypes";
import { generateSlug } from "../utils/blogHelpers";

class BlogService {
  // Collection reference
  getCollection() {
    return collection(db, "blog_posts");
  }

  // Get all blog posts with optional filters and pagination
  async getBlogPosts(filters = {}, pagination = {}) {
    try {
      let q = query(this.getCollection(), orderBy("createdAt", "desc"));

      // Apply filters
      if (filters.status && filters.status !== "all") {
        q = query(q, where("status", "==", filters.status));
      }

      if (filters.category && filters.category !== "all") {
        q = query(q, where("category", "==", filters.category));
      }

      if (filters.authorId && filters.authorId !== "all") {
        q = query(q, where("authorId", "==", filters.authorId));
      }

      // Apply pagination
      if (pagination.limit) {
        q = query(q, limit(pagination.limit));
      }

      if (pagination.startAfter && pagination.lastVisible) {
        q = query(q, startAfter(pagination.lastVisible));
      }

      const querySnapshot = await getDocs(q);
      const posts = [];
      let lastVisible = null;

      querySnapshot.forEach((doc) => {
        posts.push({
          id: doc.id,
          ...doc.data(),
        });
        lastVisible = doc;
      });

      return {
        posts,
        lastVisible,
        hasMore: posts.length === (pagination.limit || 0),
      };
    } catch (error) {
      console.error("Error fetching blog posts:", error);
      throw new Error(error.message || "Failed to fetch blog posts");
    }
  }

  // Get blog post by ID
  async getBlogPostById(id) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
        };
      } else {
        throw new Error("Blog post not found");
      }
    } catch (error) {
      console.error("Error fetching blog post:", error);
      throw new Error(error.message || "Failed to fetch blog post");
    }
  }

  // Get blog post by slug
  async getBlogPostBySlug(slug) {
    try {
      const q = query(this.getCollection(), where("slug", "==", slug));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
        };
      }
      return null;
    } catch (error) {
      console.error("Error fetching blog post by slug:", error);
      throw new Error(error.message || "Failed to fetch blog post");
    }
  }

  // Create new blog post
  async createBlogPost(data) {
    try {
      const slug = data.slug || generateSlug(data.title);

      // Check if slug exists
      const existing = await this.getBlogPostBySlug(slug);
      if (existing) {
        throw new Error("Slug already exists. Please use a different URL.");
      }

      const postData = {
        ...data,
        slug,
        status: data.status || BLOG_STATUS.DRAFT,
        viewCount: 0,
        likeCount: 0,
        commentCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        seo: {
          title: data.seo?.title || data.title,
          description: data.seo?.description || data.excerpt || "",
          keywords: data.seo?.keywords || data.tags || [],
          ogImage: data.seo?.ogImage || data.featuredImage || "",
          ogTitle: data.seo?.ogTitle || data.title,
          ogDescription: data.seo?.ogDescription || data.excerpt || "",
        },
      };

      const docRef = await addDoc(this.getCollection(), postData);
      const newDoc = await getDoc(docRef);

      return {
        id: newDoc.id,
        ...newDoc.data(),
      };
    } catch (error) {
      console.error("Error creating blog post:", error);
      throw new Error(error.message || "Failed to create blog post");
    }
  }

  // Update blog post
  async updateBlogPost(id, data) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Blog post not found");
      }

      // Check slug uniqueness if changed
      if (data.slug) {
        const existing = await this.getBlogPostBySlug(data.slug);
        if (existing && existing.id !== id) {
          throw new Error("Slug already exists. Please use a different URL.");
        }
      }

      const updateData = {
        ...data,
        updatedAt: serverTimestamp(),
      };

      // Update SEO fields if needed
      const currentData = docSnap.data();
      if (updateData.title || updateData.excerpt || updateData.featuredImage) {
        updateData.seo = {
          ...currentData.seo,
          title: updateData.seo?.title || updateData.title || currentData.title,
          description:
            updateData.seo?.description ||
            updateData.excerpt ||
            currentData.excerpt,
          ogImage:
            updateData.seo?.ogImage ||
            updateData.featuredImage ||
            currentData.featuredImage,
          ogTitle:
            updateData.seo?.ogTitle || updateData.title || currentData.title,
          ogDescription:
            updateData.seo?.ogDescription ||
            updateData.excerpt ||
            currentData.excerpt,
        };
      }

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      console.error("Error updating blog post:", error);
      throw new Error(error.message || "Failed to update blog post");
    }
  }

  // Delete blog post
  async deleteBlogPost(id) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();

        // Delete featured image from storage if exists
        if (data.featuredImage) {
          try {
            const imageRef = ref(storage, data.featuredImage);
            await deleteObject(imageRef);
          } catch (e) {
            console.log("Image not found or already deleted:", e.message);
          }
        }

        // Delete all images in the post
        if (data.images && data.images.length > 0) {
          for (const imageUrl of data.images) {
            try {
              // Extract path from URL
              const imagePath = imageUrl.split("/")?.pop()?.split("?")?.[0];
              if (imagePath) {
                const imageRef = ref(storage, `blog/${imagePath}`);
                await deleteObject(imageRef);
              }
            } catch (e) {
              console.log("Image not found or already deleted:", e.message);
            }
          }
        }

        // Delete the post document
        await deleteDoc(docRef);
      }
    } catch (error) {
      console.error("Error deleting blog post:", error);
      throw new Error(error.message || "Failed to delete blog post");
    }
  }

  // Publish blog post
  async publishBlogPost(id) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Blog post not found");
      }

      const updateData = {
        status: BLOG_STATUS.PUBLISHED,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await updateDoc(docRef, updateData);

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      console.error("Error publishing blog post:", error);
      throw new Error(error.message || "Failed to publish blog post");
    }
  }

  // Archive blog post
  async archiveBlogPost(id) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Blog post not found");
      }

      await updateDoc(docRef, {
        status: BLOG_STATUS.ARCHIVED,
        updatedAt: serverTimestamp(),
      });

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      console.error("Error archiving blog post:", error);
      throw new Error(error.message || "Failed to archive blog post");
    }
  }

  // Increment view count (manual approach)
  async incrementViewCount(id) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentViews = currentData.viewCount || 0;

        await updateDoc(docRef, {
          viewCount: currentViews + 1,
          updatedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error("Error incrementing view count:", error);
      // Don't throw here - this is a non-critical operation
    }
  }

  // Like a post
  async likePost(id) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentLikes = currentData.likeCount || 0;

        await updateDoc(docRef, {
          likeCount: currentLikes + 1,
          updatedAt: serverTimestamp(),
        });

        const updatedDoc = await getDoc(docRef);
        return {
          id: updatedDoc.id,
          ...updatedDoc.data(),
        };
      }
    } catch (error) {
      console.error("Error liking post:", error);
      throw new Error(error.message || "Failed to like post");
    }
  }

  // Unlike a post
  async unlikePost(id) {
    try {
      const docRef = doc(db, "blog_posts", id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentData = docSnap.data();
        const currentLikes = currentData.likeCount || 0;

        await updateDoc(docRef, {
          likeCount: Math.max(0, currentLikes - 1),
          updatedAt: serverTimestamp(),
        });

        const updatedDoc = await getDoc(docRef);
        return {
          id: updatedDoc.id,
          ...updatedDoc.data(),
        };
      }
    } catch (error) {
      console.error("Error unliking post:", error);
      throw new Error(error.message || "Failed to unlike post");
    }
  }

  // Add comment
  async addComment(postId, commentData) {
    try {
      const docRef = doc(db, "blog_posts", postId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Blog post not found");
      }

      const currentData = docSnap.data();
      const currentComments = currentData.commentCount || 0;
      const comments = currentData.comments || [];

      const newComment = {
        id: Date.now().toString(),
        ...commentData,
        createdAt: serverTimestamp(),
        status: "pending",
        likes: 0,
      };

      await updateDoc(docRef, {
        comments: [...comments, newComment],
        commentCount: currentComments + 1,
        updatedAt: serverTimestamp(),
      });

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      console.error("Error adding comment:", error);
      throw new Error(error.message || "Failed to add comment");
    }
  }

  // Delete comment
  async deleteComment(postId, commentId) {
    try {
      const docRef = doc(db, "blog_posts", postId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Blog post not found");
      }

      const currentData = docSnap.data();
      const comments = currentData.comments || [];
      const filteredComments = comments.filter((c) => c.id !== commentId);

      await updateDoc(docRef, {
        comments: filteredComments,
        commentCount: Math.max(0, (currentData.commentCount || 0) - 1),
        updatedAt: serverTimestamp(),
      });

      const updatedDoc = await getDoc(docRef);
      return {
        id: updatedDoc.id,
        ...updatedDoc.data(),
      };
    } catch (error) {
      console.error("Error deleting comment:", error);
      throw new Error(error.message || "Failed to delete comment");
    }
  }

  // Upload image
  async uploadImage(file, path = "blog/") {
    try {
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
      const fileName = `${path}${timestamp}_${sanitizedFileName}`;
      const storageRef = ref(storage, fileName);

      // Upload file
      const uploadTask = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(uploadTask.ref);

      return {
        url: downloadURL,
        path: fileName,
        name: file.name,
        size: file.size,
        type: file.type,
      };
    } catch (error) {
      console.error("Error uploading image:", error);
      throw new Error(error.message || "Failed to upload image");
    }
  }

  // Upload multiple images
  async uploadMultipleImages(files, path = "blog/") {
    try {
      const uploadPromises = files.map((file) => this.uploadImage(file, path));
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error("Error uploading images:", error);
      throw new Error(error.message || "Failed to upload images");
    }
  }

  // Delete image
  async deleteImage(imagePath) {
    try {
      if (!imagePath) return;
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
    } catch (error) {
      console.error("Error deleting image:", error);
      throw new Error(error.message || "Failed to delete image");
    }
  }

  // Get blog statistics
  async getBlogStats() {
    try {
      const querySnapshot = await getDocs(this.getCollection());
      const posts = [];
      querySnapshot.forEach((doc) => {
        posts.push(doc.data());
      });

      const total = posts.length;
      const statusCounts = {
        draft: 0,
        published: 0,
        scheduled: 0,
        archived: 0,
        pending_review: 0,
      };

      let totalViews = 0;
      let totalLikes = 0;
      let totalComments = 0;

      posts.forEach((post) => {
        if (statusCounts[post.status] !== undefined) {
          statusCounts[post.status]++;
        }
        totalViews += post.viewCount || 0;
        totalLikes += post.likeCount || 0;
        totalComments += post.commentCount || 0;
      });

      return {
        total,
        ...statusCounts,
        totalViews,
        totalLikes,
        totalComments,
        averageViews: total > 0 ? Math.round(totalViews / total) : 0,
        averageLikes: total > 0 ? Math.round(totalLikes / total) : 0,
        averageComments: total > 0 ? Math.round(totalComments / total) : 0,
      };
    } catch (error) {
      console.error("Error fetching blog stats:", error);
      throw new Error(error.message || "Failed to fetch blog stats");
    }
  }

  // Get published posts (for public view)
  async getPublishedPosts(limitCount = 10) {
    try {
      const q = query(
        this.getCollection(),
        where("status", "==", BLOG_STATUS.PUBLISHED),
        orderBy("publishedAt", "desc"),
        limit(limitCount),
      );

      const querySnapshot = await getDocs(q);
      const posts = [];
      querySnapshot.forEach((doc) => {
        posts.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return posts;
    } catch (error) {
      console.error("Error fetching published posts:", error);
      throw new Error(error.message || "Failed to fetch published posts");
    }
  }

  // Get posts by category
  async getPostsByCategory(category, limitCount = 10) {
    try {
      const q = query(
        this.getCollection(),
        where("category", "==", category),
        where("status", "==", BLOG_STATUS.PUBLISHED),
        orderBy("publishedAt", "desc"),
        limit(limitCount),
      );

      const querySnapshot = await getDocs(q);
      const posts = [];
      querySnapshot.forEach((doc) => {
        posts.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return posts;
    } catch (error) {
      console.error("Error fetching posts by category:", error);
      throw new Error(error.message || "Failed to fetch posts by category");
    }
  }

  // Get related posts
  async getRelatedPosts(postId, category, tags = [], limitCount = 3) {
    try {
      const q = query(
        this.getCollection(),
        where("status", "==", BLOG_STATUS.PUBLISHED),
        where("category", "==", category),
        limit(limitCount + 1), // +1 to filter out the current post
      );

      const querySnapshot = await getDocs(q);
      const posts = [];
      querySnapshot.forEach((doc) => {
        if (doc.id !== postId) {
          posts.push({
            id: doc.id,
            ...doc.data(),
          });
        }
      });

      // Sort by relevance (tags match)
      if (tags.length > 0) {
        posts.sort((a, b) => {
          const aTags = a.tags || [];
          const bTags = b.tags || [];
          const aMatches = tags.filter((t) => aTags.includes(t)).length;
          const bMatches = tags.filter((t) => bTags.includes(t)).length;
          return bMatches - aMatches;
        });
      }

      return posts.slice(0, limitCount);
    } catch (error) {
      console.error("Error fetching related posts:", error);
      return []; // Return empty array on error
    }
  }
}

const blogService = new BlogService();
export default blogService;
